import { ResourceTypeDto, ResourceTypeDtoMetaTypeEnum } from '@jooxter/api';
import { JxtCollapsibleTree } from '@jooxter/ui';
import { IJxtTreeNode } from '@jooxter/ui/src/components/JxtTree/types';
import { useTranslation } from 'react-i18next';
import { useFoldedFilters } from '../../hooks/useFoldedFilters';
import { IJxtSpacesTypesFilter } from './types';
import { useResourceTypeRepository } from '@jooxter/resource-types';

const META_TYPE_ORDER: ResourceTypeDtoMetaTypeEnum[] = [
  ResourceTypeDtoMetaTypeEnum.Meeting,
  ResourceTypeDtoMetaTypeEnum.Desk,
  ResourceTypeDtoMetaTypeEnum.Booth,
  ResourceTypeDtoMetaTypeEnum.Bubble,
  ResourceTypeDtoMetaTypeEnum.Parking,
  ResourceTypeDtoMetaTypeEnum.Vehicle,
  ResourceTypeDtoMetaTypeEnum.Visio,
  ResourceTypeDtoMetaTypeEnum.Other,
];

export const JxtSpacesTypesFilter = ({
  facetResourceTypes,
  selectedOptions,
  onChange,
  scope,
}: IJxtSpacesTypesFilter) => {
  const disabledValues = facetResourceTypes?.filter((frt) => frt?.selectable === false).map((frt) => frt?.id);
  const { t } = useTranslation();
  const { repository } = useResourceTypeRepository();
  const allResourceTypes = repository.findAll();

  const { folded, onFoldChange } = useFoldedFilters(scope, 'spaceType');
  const nullComponent = !facetResourceTypes && (!allResourceTypes || allResourceTypes.length === 0);

  if (nullComponent) {
    return null;
  }

  const resourceTypes = (facetResourceTypes ?? allResourceTypes)
    ?.map((frt) => (frt?.id ? repository.findById(frt.id) : null))
    .filter((rt): rt is ResourceTypeDto => !!rt);

  const grouped = resourceTypes?.reduce<Record<string, { id: number; name: string }[]>>((acc, rt) => {
    const key = rt.metaType ?? ResourceTypeDtoMetaTypeEnum.Other;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push({ id: rt.id, name: rt.name });
    return acc;
  }, {});

  const hasMultipleGroups = Object.keys(grouped).length > 1;

  const isDisabled = (id: number) => disabledValues?.includes(id) ?? false;

  const nodes: IJxtTreeNode[] = hasMultipleGroups
    ? META_TYPE_ORDER.filter((mt) => !!grouped[mt]).map((mt) => {
        const children = grouped[mt].map((rt) => ({
          value: String(rt.id),
          label: rt.name,
          disabled: isDisabled(rt.id),
        }));
        return {
          value: `meta-${mt}`,
          label: t<string>(`resource-type.meta-type.${mt.toLowerCase()}`),
          children,
          disabled: children.length > 0 && children.every((c) => c.disabled),
        };
      })
    : resourceTypes.map((rt) => ({
        value: String(rt.id),
        label: rt.name,
        disabled: isDisabled(rt.id),
      }));

  const allDisabled = nodes.length === 0 || (nodes.length > 0 && nodes.every((n) => n.disabled));

  const checkedValue = selectedOptions.map(String);

  const onCheckedChange = (checked: string[]) => {
    if (onChange) {
      onChange(checked.filter((v) => !v.startsWith('meta-')).map(Number));
    }
  };

  return (
    <div className="sm:px-6 py-4 border-b border-neutral-20">
      <JxtCollapsibleTree
        defaultFolded={allDisabled ? true : folded}
        disabled={allDisabled}
        onFoldChange={(e) => onFoldChange(e)}
        label={t<string>('resourcesearch-label-select-resource-types')}
        allOrNoneSelectedOptionsLabel={t<string>('collapsible.checkbox.list-all')}
        nodes={nodes}
        checkedValue={checkedValue}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
};
